import React, { memo, useCallback, useState } from "react";
import { Box, Center, Image } from "@chakra-ui/react";
import colors from "app/colors";
import { delay } from "modules/utils/Functions";
import FileFooterIcon from "./file-footer-icon";
import FileTypeThumb from "./file-type-thumb";
import FileTypeIcon from "./file-type-icon";

interface IProps {
  contentType: string;
  name: string;
  url?: string | null;
  size?: [number, number];
  opacity?: number;
  displayFileFooterIcon?: boolean;
  fileFooterIconSize?: number;
  objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
}

const BORDER_RADIUS = 4;
const MAX_LOAD_ATTEMPTS = 10;

const FileThumb = ({
  contentType,
  name,
  url,
  size = [86, 86],
  opacity = 1,
  displayFileFooterIcon = true,
  fileFooterIconSize = 6,
  objectFit = "cover",
}: IProps) => {
  const [errorCount, setErrorCount] = useState(0);

  const width = `${size[0]}px`;
  const height = `${size[1]}px`;
  const fileTypeIconWidth = Math.ceil(size[0] / 1.5);
  const fileTypeIconHeight = Math.ceil(size[1] / 1.5);

  const onLoadError = useCallback(async () => {
    if (errorCount >= MAX_LOAD_ATTEMPTS) return;
    await delay(1000);
    setErrorCount((state) => state + 1);
  }, [errorCount]);

  return (
    <Box
      borderRadius={BORDER_RADIUS}
      borderWidth={1}
      borderStyle="solid"
      borderColor={colors.grayPrimary}
    >
      <FileTypeThumb
        size={size}
        bottomRightIcon={
          displayFileFooterIcon ? (
            <FileFooterIcon
              fill={colors.white}
              contentType={contentType}
              radius={BORDER_RADIUS}
              size={fileFooterIconSize}
            />
          ) : undefined
        }
        bottomRightIconSize={fileFooterIconSize}
        centerImage={
          <Image
            onError={onLoadError}
            src={url || undefined}
            alt={name}
            w={width}
            h={height}
            gridColumn={1}
            gridRow={1}
            borderRadius={BORDER_RADIUS}
            objectFit={objectFit}
            opacity={opacity}
            bg={colors.white}
            fallback={
              <Center
                w={width}
                h={height}
                gridColumn={1}
                gridRow={1}
                bg={colors.white}
                borderRadius={BORDER_RADIUS}
              >
                <FileTypeIcon
                  contentType={contentType}
                  size={[fileTypeIconWidth, fileTypeIconHeight]}
                  fill={colors.gray0}
                />
              </Center>
            }
          />
        }
      />
    </Box>
  );
};

export default memo(FileThumb);
